import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useHistory } from 'react-router-dom';
import { useSubscription } from 'react-stomp-hooks';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';
import { getStringWithoutHash } from 'utilitys/helpers/general';

import useCustomPageSubTitle from 'hooks/useCustomPageSubTitle';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import { useReactTableState } from 'hooks/useReactTableState';
import useTableCurrentState from 'hooks/useTableCurrentState';
import useUserHook from 'hooks/useUserHook';

import { useBattleCardsStore } from 'store/battlecards.store';
import { useVersionStore } from 'store/version.store';

import { useMutationDownloadCsvFile } from 'api/mutations/downloadCsvFile.mutation';
import { useMutationExportXerOrMpp } from 'api/mutations/exportXerOrMpp.mutation';
import { useMutationRowsBoardToCsv } from 'api/mutations/extractRowsBoardToCsv.mutation';
import { useMutationRowsBoardToXls } from 'api/mutations/extractRowsBoardToXls.mutation';
import { ISingleSmallCardResponse, useQueryListCardsData } from 'api/queries/getListFilteredCards.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import {
    pptxTableNarrativeHeaders,
    serializedOneLevelDataStructureTableToPPTX,
} from 'components/common/ExportDropdown/exportDropdown.utils';
import Icon from 'components/common/Icons/icon';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { serializeColumnsVisibility } from 'components/common/Tables/Tables.utils';
import { DisplayStyle } from 'components/common/ToggleView/ToggleView';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { CustomPagination } from 'components/common/pagination/customPagination';
import { TitleWithCounter } from 'components/common/titleWithCounter/titleWithCounter';

import { excellenceAppsConfig } from 'components/Dashboards/ExcellenceApps/excellenceAppsConfig';
import { isFiltersContainStatusChanged } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoard.utils';
import {
    columnsV8,
    generateFlags,
    initialState,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoardColumns.utils';
import { XlsExportModal } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/XlsExportModal/XlsExportModal';
import { useQueryCountersData } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/queries/battleCardsQuery';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { useMutationSetWidgetColumns } from 'components/Dashboards/Project/Components/CustomDashboard/queries/useQuerySelectedTableColumns';
import { IRowsBoard } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { CustomizedButton, CustomizedRoundIcon, CustomizedTooltip } from 'components/common';

import classes from './RowsBoard.module.scss';

const RowsBoard = ({
    widgetId,
    filter,
    projectId,
    contractId,
    externalComponents,
    editNarrative,
    latestVersionId,
    setSubTitleForExport,
}: IRowsBoard) => {
    const { filterData } = useBattleCardsStore();
    const { data: counters } = useQueryCountersData(filterData);
    const { ability, email } = useUserHook();
    const [isXlsExportModal, setIsXlsExportModal] = useState<boolean>(false);
    const {
        pagination,
        setPagination,
        sorting,
        setSorting,
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        rowSelection,
        setRowSelection,
    } = useReactTableState({ initialSorting: widgetId && filter?.filters ? filter?.filters.sortingRequestList : [] });
    const title = 'Activities Segment';
    const subTitle = useCustomPageSubTitle({ projectId, contractId, latestVersionId });
    const history = useHistory();
    const currentVersion = useVersionStore((state) => state.version);
    const [totalPages, setTotalPages] = useState(0);

    const { displayStyle } = useBattleCardsStore();
    const { sendEventWithDimensions } = useEventWithDimensions();
    // FOR PinBoard Only
    const route = `${getStringWithoutHash(window.location.href)}`;
    const [modalColumnsOpen, setModalColumnsOpen] = useState<boolean>(false);
    const { mutate: downloadCsvFile } = useMutationDownloadCsvFile();

    useSubscription(email ? [`/topic/${email}.notifications.push`] : [], (response) => {
        const { type, message } = JSON.parse(response.body);
        if (type === 'CsvFileReady') {
            setIsDownloadCsv(false);
            const { fileName } = message;
            if (fileName) {
                downloadCsvFile({ fileName, downloadName: `${title}_${subTitle}` });
                sendEventWithDimensions({
                    category: 'Export',
                    action: 'CSV',
                    label: `${title} ${subTitle}`,
                });
            }
        }
    });

    //  reset Pagination on sorting and new filter request
    useEffect(() => {
        setPagination({ ...pagination, page: 1 });
    }, [sorting, filterData]);

    const cardsDataFilters = useMemo(() => {
        const filtersObject = filter?.filters.hasOwnProperty('cardCachedFilter')
            ? filter?.filters.cardCachedFilter
            : filter?.filters;
        return widgetId
            ? {
                  ...filter?.filters,
                  cardCachedFilter: {
                      ...filtersObject,
                      filter: {
                          filters: filtersObject.filter?.filters.map((filter) => {
                              if (filter.name === 'VERSION_CARD_BOARD') {
                                  return {
                                      ...filter,
                                      data: [latestVersionId],
                                  };
                              } else {
                                  return filter;
                              }
                          }),
                      },
                  },
                  paging: { ...pagination, page: pagination.page - 1 },
                  sortingRequestList: sorting,
              }
            : {
                  cardCachedFilter: filterData,
                  paging: { ...pagination, page: pagination.page - 1 },
                  sortingRequestList: sorting,
              };
    }, [filterData, filter, widgetId, latestVersionId, pagination, sorting]);

    const pinBoardFilterData = useMemo(() => {
        return {
            cardCachedFilter: filterData,
            paging: { ...pagination, page: pagination.page - 1 },
            sortingRequestList: sorting,
        };
    }, [filterData, pagination, sorting]);

    const isStatusChangedFilterSelected = isFiltersContainStatusChanged(filterData);

    const { data: cards, isLoading, isFetching } = useQueryListCardsData(cardsDataFilters);

    const boundaryRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (cards) {
            setTotalPages(cards.numOfPages);
        }
    }, [cards]);

    // =============================================================================================================

    const pptxData = serializedOneLevelDataStructureTableToPPTX({
        data: cards?.cardList,
        sortBy: { key: 'endDate', sortOrder: 'asc' },
        headers: pptxTableNarrativeHeaders,
    });

    const excludingFilters = ['PROJECT_CARD_BOARD', 'CONTRACT_CARD_BOARD', 'VERSION_CARD_BOARD'];

    const backwardCompatibleFilter = filter?.filters.cardCachedFilter
        ? filter?.filters.cardCachedFilter.filter
        : filter?.filters?.filter;
    const { generateFilters: formattedFilter } = useGlobalFiltersHook({
        widgetId,
        filter: backwardCompatibleFilter,
        latestVersionId,
    });
    const backwardCompatibleFlags = filter?.filters.cardCachedFilter
        ? filter?.filters.cardCachedFilter.flags
        : filter?.filters.flags;
    const flags = generateFlags(backwardCompatibleFlags);

    const pills = [...flags, ...formattedFilter.filter((item) => !excludingFilters.includes(item.name))];

    const componentKey = 'battlecardsRowsBoard';
    const id = 'battlecardsRowsBoard';
    const updatedInitialState = useTableCurrentState({ initialState, componentKey, widgetId });

    const { mutate: csvMutate } = useMutationRowsBoardToCsv();
    const { mutate: xlsMutate, isLoading: xlsMutateLoading } = useMutationRowsBoardToXls();
    const { mutate: xerOrMppMutate, isLoading: xerOrMppMutateLoading } = useMutationExportXerOrMpp();
    const [isDownloadCsv, setIsDownloadCsv] = useState<boolean>(false);

    const exportIsLoading = isDownloadCsv || xlsMutateLoading || xerOrMppMutateLoading;

    const { handleSnackBar } = useCustomSnackBar();
    const { mutate: mutateSetWidgetColumns } = useMutationSetWidgetColumns();

    const handleAPICsvExportPrivilege = ability.can('view', 'FileDownload');

    const handleAPICsvExport = ({ isCompact = false }: { isCompact?: boolean }) => {
        setIsDownloadCsv(true);
        csvMutate({
            filter: cardsDataFilters.cardCachedFilter,
            fileName: `${title}_${subTitle}`,
            isCompact: isCompact,
        });
    };

    const handleAPIXlsExport = (data) => {
        xlsMutate(
            {
                filter: cardsDataFilters.cardCachedFilter,
                fileName: `${title}_${subTitle}`,
                configRequest: {
                    contractId: data.contractId,
                    isExtractRemainingDuration: data.remainingDurationInDays,
                    isExtractPercentageComplete: data.submittedDurationComplete,
                    userData: data.userData,
                },
            },
            {
                onSuccess: () => {
                    handleSnackBar('File Successfully Downloaded', 'success');

                    sendEventWithDimensions({
                        category: 'Export',
                        action: 'XLS',
                        label: `${title} ${subTitle}`,
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    const handleExrExport = () => {
        if (latestVersionId) {
            xerOrMppMutate(
                {
                    metaDataId: latestVersionId,
                    exportType: 'XER',
                    fileName: `${title}_${subTitle}`,
                },
                {
                    onSuccess: () => {
                        handleSnackBar('File Successfully Downloaded', 'success');

                        sendEventWithDimensions({
                            category: 'Export',
                            action: 'XER',
                            label: `${title} ${subTitle}`,
                        });
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    const handleMppExport = () => {
        if (latestVersionId) {
            xerOrMppMutate(
                {
                    metaDataId: latestVersionId,
                    exportType: 'MPP',
                    fileName: `${title}_${subTitle}`,
                },
                {
                    onSuccess: () => {
                        handleSnackBar('File Successfully Downloaded', 'success');

                        sendEventWithDimensions({
                            category: 'Export',
                            action: 'MPP',
                            label: `${title} ${subTitle}`,
                        });
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    const setWidgetColumns = (widgetId: string) => {
        if (tableInstance) {
            mutateSetWidgetColumns(
                { columns: { hiddenColumns: tableInstance.state.hiddenColumns, columnOrder }, widgetId },
                {
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    useEffect(() => {
        setSubTitleForExport && setSubTitleForExport({ widgetId, subTitle, title });
    }, [widgetId, subTitle, title]);

    useEffect(() => {
        updatedInitialState && setColumnOrder(updatedInitialState.columnOrder ?? []);
        updatedInitialState &&
            setColumnVisibility(
                serializeColumnsVisibility({ columns: columnsV8, hiddenColumns: updatedInitialState.hiddenColumns }) ??
                    {},
            );
    }, [updatedInitialState]);

    const tableInstance = useMemo(() => {
        const hiddenColumns = Object.keys(columnVisibility).reduce((acc: string[], item: string) => {
            columnVisibility[item] === false && acc.push(item);
            return acc;
        }, []);

        return {
            setColumnOrder,
            setHiddenColumns: (hiddenColumns) =>
                setColumnVisibility(serializeColumnsVisibility({ columns: columnsV8, hiddenColumns })),
            state: {
                hiddenColumns,
            },
            allColumns: columnOrder
                .map((item) => {
                    const column = columnsV8.find((column) => column.id === item);
                    if (column) {
                        return {
                            ...column,
                            isVisible: !hiddenColumns.includes(column.id),
                        };
                    }
                    return null;
                })
                .filter((x) => x),
        };
    }, [columnVisibility, columnOrder]);

    if (displayStyle === DisplayStyle.COLUMNS && !widgetId) return null;
    return (
        <WidgetWithTitle
            id={id}
            title={<TitleWithCounter title={title} counter={cards?.numOfAllCards} />}
            titleComponents={[
                isStatusChangedFilterSelected ? (
                    <CustomizedButton
                        size={'large'}
                        color={'primary'}
                        onClick={() => {
                            if (currentVersion) {
                                history.push(
                                    AppRoutes.buildPath(
                                        AppRoutes.DASHBOARD_EXCELLENCE_APPS_PATH,
                                        excellenceAppsConfig.scheduleSimulation.link,
                                        currentVersion.versionHashCode,
                                    ),
                                );
                            }
                        }}
                    >
                        Run Simulation
                    </CustomizedButton>
                ) : null,
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <CustomizedTooltip
                    key={'tableColumns'}
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={'Customize table columns'}
                    triggerElement={
                        <CustomizedRoundIcon enableHover={true} onClick={() => setModalColumnsOpen(true)}>
                            <Icon name={'tune-filter'} />
                        </CustomizedRoundIcon>
                    }
                />,
                <AddWidget
                    key={'AddWidget'}
                    title={title}
                    componentKey={componentKey}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    filters={pinBoardFilterData}
                    callBack={setWidgetColumns}
                    localFilters={{ counters }}
                />,
                <ExportDropdown
                    key={'ExportDropdown'}
                    title={title}
                    pptxData={pptxData}
                    ref={boundaryRef}
                    subTitle={subTitle}
                    handleAPICsvExport={handleAPICsvExportPrivilege ? handleAPICsvExport : undefined}
                    handleAPICsvExportCompact={() => {
                        handleAPICsvExport({ isCompact: true });
                    }}
                    handleAPIXlsExport={() => setIsXlsExportModal(true)}
                    handleExrExport={handleExrExport}
                    handleMppExport={handleMppExport}
                />,
            ]}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId ? <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={pills} /> : null,
            ]}
        >
            <div className={classes.rowsBoardWrapper} ref={boundaryRef}>
                {updatedInitialState && (
                    <>
                        <TableV8<ISingleSmallCardResponse>
                            data={cards?.cardList || []}
                            columns={columnsV8}
                            isLoading={isLoading || isFetching || exportIsLoading}
                            noData={<NoData>No activities match the applied filter</NoData>}
                            sorting={sorting}
                            setSorting={setSorting}
                            columnVisibility={columnVisibility}
                            setColumnVisibility={setColumnVisibility}
                            columnOrder={columnOrder}
                            setColumnOrder={setColumnOrder}
                            rowSelection={rowSelection}
                            setRowSelection={setRowSelection}
                        />
                        <div className={classes.pagination}>
                            <CustomPagination
                                totalPages={totalPages}
                                pagination={pagination}
                                setPagination={setPagination}
                                isDisabled={isFetching}
                            />
                        </div>
                    </>
                )}
            </div>

            <CustomTableColumnsModal
                onHideModal={() => setModalColumnsOpen(false)}
                showModal={modalColumnsOpen}
                widgetId={widgetId}
                componentKey={componentKey}
                tableInstance={tableInstance}
                initialState={initialState}
                columns={columnsV8}
                onChangeOrder={setColumnOrder}
                onChangeVisibility={setColumnVisibility}
            />

            <XlsExportModal
                isXlsExportModal={isXlsExportModal}
                setIsXlsExportModal={setIsXlsExportModal}
                handleAPIXlsExport={handleAPIXlsExport}
            />
        </WidgetWithTitle>
    );
};

export default React.memo(RowsBoard);
